<template>
  <div class="store-status">
    <div
      class="ss-indicator"
      :class="`ss-indicator--${handleTimes.status}`"
      data-allow-mismatch
    />
    <fn-headline
      class="ss-title"
      color="grey-dark"
      type="div"
      size="h4"
    >
      {{ handleTimes.text }}
    </fn-headline>
  </div>
</template>

<script lang="ts">
import { DateTime } from 'luxon';
import { formatDateStringShortTime, formatLocaleDate, dateIsToday, addDaysToNow, datesAreSame } from '@/utils/date';

export default defineComponent({
  props: {
    store: {
      type: Object,
      default: () => {},
    },
  },
  computed: {
    ...mapState(useMainStore, {
      baseStore: 'baseStore',
    }),

    openingHoursToday() {
      const { specialDayOpeningList, weekDayOpeningList } = this.store.openingHours;

      if (specialDayOpeningList?.length) {
        const openingHoursToday = specialDayOpeningList.find(({ date }) => dateIsToday(date));

        if (openingHoursToday) {
          return openingHoursToday;
        }
      }

      if (!weekDayOpeningList?.length) {
        return null;
      }

      const dayOfWeek = this.currentDayIndex();

      return weekDayOpeningList[dayOfWeek];
    },

    nextWorkingDay() {
      const { specialDayOpeningList, weekDayOpeningList } = this.store.openingHours;

      // early-exit for indefinitely closed shops (e.g. down for renovation)
      const mergedDates = [...(specialDayOpeningList ?? []), ...(weekDayOpeningList ?? [])];
      const notIndefinitelyClosed = !!mergedDates.find((day) => !day.closed);

      if (!notIndefinitelyClosed) {
        return null;
      }

      let nextWorkingDayIndex = this.currentDayIndex() + 1;
      let nextWorkingDay;
      // find next non-closed day
      for (let day = nextWorkingDayIndex; day < nextWorkingDayIndex + 7; day += 1) {
        const dayOffset = day % 7;
        const dayData = this.specialDay(day - nextWorkingDayIndex) || weekDayOpeningList[dayOffset];

        if (!dayData.closed) {
          nextWorkingDay = dayData;
          nextWorkingDayIndex = dayOffset;
          break;
        }
      }

      // if day is missing openingTime display as closed
      if (!nextWorkingDay?.openingTime) {
        return null;
      }

      return `${this.$t('weekDays')[nextWorkingDayIndex]} ${nextWorkingDay?.openingTime?.formattedHour}`;
    },

    storeWithPause() {
      return this.openingHoursToday.startPauseTime && this.openingHoursToday.endPauseTime;
    },

    calculateLocalNowTime() {
      const storeTimezone = getTimezone(this.baseStore);

      const userCurrentLocalTime = DateTime.local();
      const storeTime = DateTime.local().setZone(storeTimezone);

      const storeTimeStamp = storeTime.valueOf();
      const storeTimeOffsetInMs = storeTime.offset * 60000;
      const userTimeOffsetInMs = userCurrentLocalTime.offset * 60000;

      const nowAdjusted = storeTimeStamp + storeTimeOffsetInMs - userTimeOffsetInMs;

      return nowAdjusted;
    },

    handleTimes() {
      const TIME_BUFFER_MINUTES = 30;
      const now = this.calculateLocalNowTime;

      const activeSinceDate = new Date(this.store.activeSince);

      if (activeSinceDate.getTime() > now) {
        return {
          text: this.$t('store.openingHours.openingPlanned', {
            date: formatLocaleDate(activeSinceDate),
            time: formatDateStringShortTime(activeSinceDate),
          }),
          status: 'inactive',
        };
      }

      const openingTime = this.getTimestampForTodayAtTime(this.openingHoursToday.openingTime?.formattedHour);
      const closingTime = this.getTimestampForTodayAtTime(this.openingHoursToday.closingTime?.formattedHour);
      if (this.openingHoursToday.closed || now >= closingTime) {
        if (this.nextWorkingDay) {
          // today closed - next day is open
          return {
            text: this.$t('store.openingHours.closedUntil', { time: this.nextWorkingDay }),
            status: 'closed',
          };
        }
        // today closed - next day is also closed
        return {
          text: this.$t('store.openingHours.closedToday'),
          status: 'closed',
        };
      }

      if (this.storeWithPause) {
        const pauseStartTime = this.getTimestampForTodayAtTime(this.openingHoursToday.startPauseTime?.formattedHour);
        const pauseStartTimeWithBuffer = pauseStartTime - 1000 * 60 * TIME_BUFFER_MINUTES;
        const pauseEndTime = this.getTimestampForTodayAtTime(this.openingHoursToday.endPauseTime?.formattedHour);
        const pauseEndTimeWithBuffer = pauseEndTime - 1000 * 60 * TIME_BUFFER_MINUTES;

        // pauseStartTime is more than 30 minutes away
        if (now < pauseStartTimeWithBuffer) {
          return {
            text: this.$t('store.openingHours.closingSoon', {
              time: this.openingHoursToday.startPauseTime.formattedHour,
            }),
            status: 'opened',
          };
        }

        // pauseStartTime is less then 30 min away
        if (now > pauseStartTimeWithBuffer && now < pauseStartTime) {
          return {
            text: this.$t('store.openingHours.closingSoon', {
              time: this.openingHoursToday.startPauseTime.formattedHour,
            }),
            status: 'closing-soon',
          };
        }

        // break and pauseEndTime is more than 30 min away
        if (now > pauseStartTime && now < pauseEndTimeWithBuffer) {
          return {
            text: this.$t('store.openingHours.closedUntil', {
              time: this.openingHoursToday.endPauseTime.formattedHour,
            }),
            status: 'closed',
          };
        }

        // break and pauseEndTime is less than 30 min away
        if (now > pauseEndTimeWithBuffer && now < pauseEndTime) {
          return {
            text: this.$t('store.openingHours.openSoonTime', {
              time: this.openingHoursToday.endPauseTime.formattedHour,
            }),
            status: 'opened-soon',
          };
        }
      }

      const openingTimeWithBuffer = openingTime - 1000 * 60 * TIME_BUFFER_MINUTES;
      const closingTimeWithBuffer = closingTime - 1000 * 60 * TIME_BUFFER_MINUTES;

      // openingTime is less than 30 minutes away
      if (now > openingTimeWithBuffer && now < openingTime) {
        return {
          text: this.$t('store.openingHours.closedUntil', { time: this.openingHoursToday.openingTime.formattedHour }),
          status: 'opened-soon',
        };
      }

      // openingTime is more than 30 minutes away
      if (now < openingTime) {
        return {
          text: this.$t('store.openingHours.closedUntil', { time: this.openingHoursToday.openingTime.formattedHour }),
          status: 'closed',
        };
      }

      // closingTime is more than 30 away
      if (now < closingTimeWithBuffer) {
        return {
          text: this.$t('store.openingHours.opened', { time: this.openingHoursToday.closingTime.formattedHour }),
          status: 'opened',
        };
      }

      // closingTime is less than 30 minutes away
      if (now >= closingTimeWithBuffer && now < closingTime) {
        return {
          text: this.$t('store.openingHours.closingSoon', { time: this.openingHoursToday.closingTime.formattedHour }),
          status: 'closing-soon',
        };
      }

      return {
        text: this.$t('store.openingHours.closedToday'),
        status: 'closed',
      };
    },
  },
  methods: {
    currentDayIndex() {
      const currentDate = new Date();

      // set current day to yesterday to fix divergence Array from BE and week-structure in JS
      currentDate.setDate(currentDate.getDate() - 1);

      return currentDate.getDay();
    },
    getTimestampForTodayAtTime(time: any) {
      if (!time) {
        return 0;
      }

      const date = new Date().toDateString();
      return new Date(`${date} ${time}`).getTime();
    },
    specialDay(dayOffset: number) {
      const dateToFind = addDaysToNow(dayOffset);
      return this.store.openingHours?.specialDayOpeningList?.find(
        ({ closed, date }) => !closed && datesAreSame(date, dateToFind),
      );
    },
  },
});
</script>

<style lang="scss" scoped>
.store-status {
  .ss-indicator {
    display: inline-block;
    width: 10px;
    height: 10px;
    background-color: palette(grey);
    border-radius: 50%;
    margin-right: math.div($pad, 2);

    &.ss-indicator--opened {
      background-color: palette(orientation, green);
    }

    &.ss-indicator--break,
    &.ss-indicator--closed {
      background-color: palette(orientation, red);
    }

    &.ss-indicator--opened-soon,
    &.ss-indicator--closing-soon {
      background-color: palette(orientation, yellow);
    }

    &.ss-indicator--inactive {
      background-color: palette(grey, dark);
    }
  }

  .ss-title {
    display: inline-block;
    margin-bottom: 0;
  }
}
</style>
